import { AppDispatch } from "../store";
import { showConfirmationDialog } from "../slices/confirmationDialogSlice";

/**
 * Shows a confirmation dialog and returns a promise that resolves to true if confirmed, false if cancelled
 * @param dispatch - Redux dispatch function
 * @param message - The message to display in the dialog
 * @param title - Optional title for the dialog (defaults to "Confirm Action")
 * @returns Promise<boolean> - true if confirmed, false if cancelled
 */
export type ConfirmDialogOptions = {
  requireReason?: boolean;
  reasonLabel?: string;
};

export const showConfirmDialog = (
  dispatch: AppDispatch,
  message: string,
  title?: string,
  options?: ConfirmDialogOptions
): Promise<boolean> => {
  return new Promise((resolve) => {
    dispatch(
      showConfirmationDialog({
        message,
        title,
        requireReason: options?.requireReason,
        reasonLabel: options?.reasonLabel,
        resolve: (confirmed) => resolve(confirmed),
      })
    );
  });
};

/** Confirm dialog that returns trimmed reason text when confirmed (required if requireReason). */
/** Single-button alert modal (styled like validation errors). */
export const showAlertDialog = (
  dispatch: AppDispatch,
  message: string,
  title = "Validation Error"
): Promise<void> => {
  return new Promise((resolve) => {
    dispatch(
      showConfirmationDialog({
        message,
        title,
        resolve: () => resolve(),
      })
    );
  });
};

export const showConfirmDialogWithReason = (
  dispatch: AppDispatch,
  message: string,
  title?: string,
  options?: ConfirmDialogOptions
): Promise<{ confirmed: boolean; reason: string }> => {
  return new Promise((resolve) => {
    dispatch(
      showConfirmationDialog({
        message,
        title,
        requireReason: options?.requireReason,
        reasonLabel: options?.reasonLabel,
        resolve: (confirmed, reason) =>
          resolve({ confirmed, reason: String(reason ?? "").trim() }),
      })
    );
  });
};
